/*!
 *
 * Angle - Bootstrap Admin Template
 *
 * Version: 4.7.5
 * Author: @themicon_co
 * Website: http://themicon.co
 * License: https://wrapbootstrap.com/help/licenses
 *
 */

import React, { Component } from "react"
import { BrowserRouter } from "react-router-dom"

// App Routes
import Routes from "./Routes"

// Vendor dependencies
import "./Vendor"
// Application Styles
import "./styles/bootstrap.scss"
import "./styles/app.scss"
import "./components/Views/FastEval/styles/ModalStyles.css"
// Feuilles de composants partagés chargées d'emblée. Selon la page, elles
// arrivaient avant ou après d'autres feuilles d'un même chunk partagé, et
// mini-css-extract-plugin devait trancher (« Conflicting order » au build).
// Leurs composants les importent toujours, sans effet puisqu'elles sont déjà là.
// Toutes leurs règles sont préfixées : leur place dans la cascade est neutre.
import "react-image-crop/dist/ReactCrop.css"
import "./components/CustomComponents/Loading/SectionLoader.css"
import "./components/ui/MultiPhotoPicker/MultiPhotoPicker.css"
import "./components/Views/HealthManagement/components/PrescriptionPresentation.css"
import "./design-system/v2/compatibility/FilterChipBridge.css"

import UpdateNotification from "./components/Common/UpdateNotification"
import StagingRibbon from "./components/Common/StagingRibbon"

// Microsoft Clarity analytics
import { initClarity, identifyClarityUser } from "./utils/clarity"
import { UserDatabaseConnector } from "./dataconnector/usersDatabaseConnector"
import { currentTabPark, installActiveParkTabSync, parkOfProfileSnapshot, rememberTabPark } from "./utils/users/activeParkSwitch"
import { reportWeeklyDigestArrival, takeWeeklyDigestRef } from "./utils/weeklyDigestArrival"
import { initFunctions } from "./tools/conf"
import { isDemoActive } from "./anonymization/demoState"
import { identifySentryUser } from "./utils/observability/sentry"

const userConnector = new UserDatabaseConnector()

class App extends Component {
  constructor(props) {
    super(props)
    // Lien venu du résumé du lundi : marque retirée avant que le routeur lise l'adresse.
    this.weeklyDigestRef = takeWeeklyDigestRef()
  }

  componentDidMount() {
    // Microsoft Clarity : opt-in. L'init n'a lieu que si l'utilisateur
    // a activé le mode diagnostic dans son compte (clarityTrackingEnabled).
    this.startClarityAuthListener()
    // Parc actif (droits multi-parcs, étape 4) : la copie du profil est commune
    // aux onglets. Quand un autre onglet change de parc, celui-ci recharge tout
    // plutôt que d'afficher l'ancien parc en écrivant dans le nouveau.
    if (!currentTabPark()) rememberTabPark(parkOfProfileSnapshot(window.localStorage.getItem("userData")))
    this.stopActiveParkTabSync = installActiveParkTabSync({ target: window, location: window.location, homePath: "/" })
  }

  componentWillUnmount() {
    if (this.unsubscribeClarityAuth) this.unsubscribeClarityAuth()
    if (this.stopActiveParkTabSync) this.stopActiveParkTabSync()
  }

  startClarityAuthListener = async () => {
    try {
      this.unsubscribeClarityAuth = await userConnector._onAuthStateChanged(async (user) => {
        if (!user) {
          identifySentryUser(null)
          return
        }
        if (this.weeklyDigestRef) {
          const ref = this.weeklyDigestRef
          this.weeklyDigestRef = null
          reportWeeklyDigestArrival(ref, { getFunctions: initFunctions, isDemo: isDemoActive })
        }
        let userData = null
        try {
          const res = await userConnector._getUserData(user.uid, false)
          userData = res?.data || null
        } catch (_) {}
        // Sentry : uid et parc actif seulement, pour tous les comptes (erreurs, pas de suivi d'usage).
        identifySentryUser(user, userData, { zooId: currentTabPark(), demo: isDemoActive() })
        if (userData?.clarityTrackingEnabled === true) {
          initClarity()
          identifyClarityUser(user, userData)
        }
      })
    } catch (_) {}
  }

  render() {
    // specify base href from env variable 'PUBLIC_URL'
    // use only if application isn't served from the root
    // for development it is forced to root only
    const basename = process.env.NODE_ENV === "development" ? "/" : process.env.PUBLIC_URL || "/"
    return (
      <BrowserRouter basename={basename}>
        <Routes />
        <UpdateNotification />
        <StagingRibbon />
      </BrowserRouter>
    )
  }
}

export default App
